/** @format */

/* eslint @typescript-eslint/prefer-string-starts-ends-with: "warn" */
import { pickBy } from 'lodash';
import qs from 'qs';

import { getConfig } from '@toucan/helpers/settings';

/**
 * Converts the legacy model url to the url of the category
 *
 * @param url The url to convert
 * @param t The translation function
 * @param isSubcategory whether or not the passed url is a subcategory or not
 * defaults to false
 * @returns {string} The url of the category on Toucan
 */
export function convertLegacyModelPageUrlToCategoryUrl(
    url: string,
    isSubcategory: boolean = false
) {
    const parts = url.split('/');
    const lang = parts.slice(0, 2);

    // Removes model slug and trailing slash
    const categoryEnd = parts.length - (isSubcategory ? 1 : 2);
    const category = parts.slice(3, categoryEnd);

    return [...lang, 'c', ...category, ''].join('/');
}

/**
 * Removes the first section of the url
 *
 * @param url The url to edit
 * @returns {string} The url with the first section removed
 */
export function removeFirstPath(url: string) {
    const parts = url.split('/');
    const startsWithSlash = url.indexOf('/') === 0;

    const start = startsWithSlash ? 2 : 1;

    const newParts = parts.slice(start, parts.length);

    if (startsWithSlash) {
        newParts.unshift('');
    }

    return newParts.join('/');
}

/**
 * Removes a trailing slash from a url
 *
 * @param url The url to remove the trailing slash from
 * @returns {string} The url without the trailing slash
 */
export function removeTrailingSlash(url: string) {
    return url.replace(/\/$/, '');
}

/**
 * Removes a slash at the begging of a url
 *
 * @param url The url to remove the slash from
 * @returns {string} The url without the initial slash
 */
export function removeInitialSlash(url: string) {
    return url.replace(/^\//, '');
}

/**
 * Prepares a canonical reference to the current page being viewed
 * ensuring that only certain URL parameters are present
 *
 * @param {string[]} allowedUrlParams An array of permitted URL params
 * @return {string} The canonical reference
 */
export function getCanonicalHref(
    allowedUrlParams: string[] = []
): string | undefined {
    if (typeof window === 'undefined') {
        return undefined;
    }

    const params = qs.parse(window.location.search, {
        ignoreQueryPrefix: true,
    });

    const preparedParams = qs.stringify(
        pickBy(params, (value: any, key: string) =>
            allowedUrlParams.includes(key)
        ),
        { addQueryPrefix: true }
    );

    return `${window.location.protocol}//${window.location.host}${window.location.pathname}${preparedParams}`;
}

export function hasPathNameChanged(pathName: string) {
    try {
        const { pathname, origin } = window.location;
        const newUrl = new URL(pathName, origin);

        return newUrl.pathname !== pathname;
    } catch (e) {
        return true;
    }
}

const { publicRuntimeConfig } = getConfig();
export function getStaticImageUrl(imagePath?: string) {
    return `${publicRuntimeConfig?.assetPrefix ?? ''}${imagePath}`;
}
